// PWA helpers — service worker registration, install prompt capture,
// browser notification permission management.
//
// Service worker is registered only in production builds; HMR in dev would
// otherwise cache stale modules and become confusing.

let installPromptEvent: BeforeInstallPromptEvent | null = null;
const installListeners = new Set<() => void>();

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

export function registerServiceWorker(): void {
  if (typeof window === 'undefined') return;
  if (!('serviceWorker' in navigator)) return;
  if (import.meta.env.DEV) {
    // A previous prod build may have registered the SW; in dev we
    // unregister it so its stale shell cache doesn't intercept HMR
    // pages and produce ERR_CONNECTION_REFUSED on the dev port.
    navigator.serviceWorker
      .getRegistrations()
      .then((regs) => Promise.all(regs.map((r) => r.unregister())))
      .catch(() => {
        /* noop */
      });
    return;
  }
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {
      /* SW not available — non-fatal */
    });
  });
}

export function listenForInstallPrompt(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    installPromptEvent = e as BeforeInstallPromptEvent;
    installListeners.forEach((cb) => cb());
  });
  window.addEventListener('appinstalled', () => {
    installPromptEvent = null;
    installListeners.forEach((cb) => cb());
  });
}

export function canPromptInstall(): boolean {
  return installPromptEvent !== null;
}

export function subscribeInstallChange(cb: () => void): () => void {
  installListeners.add(cb);
  return () => installListeners.delete(cb);
}

export async function showInstallPrompt(): Promise<'accepted' | 'dismissed' | 'unsupported'> {
  if (!installPromptEvent) return 'unsupported';
  await installPromptEvent.prompt();
  const { outcome } = await installPromptEvent.userChoice;
  installPromptEvent = null;
  installListeners.forEach((cb) => cb());
  return outcome;
}

export function isIos(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) && !('MSStream' in window);
}

export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

export function pushPermission(): NotificationPermission | 'unsupported' {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
  return Notification.permission;
}

export async function requestPushPermission(): Promise<NotificationPermission | 'unsupported'> {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
  try {
    return await Notification.requestPermission();
  } catch {
    return 'denied';
  }
}

/** True when the agent isn't actually looking at the tab — it's backgrounded,
 *  the window is minimized, or another app/window has focus. Used to decide
 *  whether to raise an OS notification (vs. relying on the in-app toast, which
 *  is enough when the tab is in front). */
export function tabInactive(): boolean {
  if (typeof document === 'undefined') return false;
  if (document.hidden) return true;
  return typeof document.hasFocus === 'function' ? !document.hasFocus() : false;
}

/** True when the public marketing landing (HashRouter root) is showing. Agent
 *  notification surfaces — OS popups and in-app toasts — are suppressed there:
 *  the landing is a marketing page (and it embeds the widget itself), so it
 *  shouldn't pop the signed-in agent's notifications. */
export function onLandingRoute(): boolean {
  if (typeof window === 'undefined') return false;
  const h = window.location.hash;
  return h === '' || h === '#' || h === '#/';
}

/** Fire an OS notification. Clicking it focuses the agent tab and (when a
 *  sessionId is given) opens that conversation. `tag` collapses repeats for the
 *  same conversation so a chatty visitor doesn't stack notifications. */
export function fireBrowserNotification(
  title: string,
  opts: { body?: string; sessionId?: string; tag?: string } = {},
): void {
  if (typeof window === 'undefined' || !('Notification' in window)) return;
  if (Notification.permission !== 'granted') return;
  // Don't pop notifications while the agent is on the marketing landing page.
  if (onLandingRoute()) return;
  try {
    const n = new Notification(title, {
      body: opts.body,
      icon: `${import.meta.env.BASE_URL}logos/openline-icon-light.svg`,
      // tag collapses notifications per conversation; renotify makes a
      // same-tag replacement re-alert (without it, follow-up messages
      // silently replace the first and the agent never sees them).
      ...(opts.tag ? { tag: opts.tag, renotify: true } : {}),
    } as NotificationOptions);
    n.onclick = () => {
      try {
        window.focus();
      } catch {
        /* noop */
      }
      // HashRouter — setting the hash navigates SPA-style to the conversation.
      if (opts.sessionId) window.location.hash = `/conversation/${opts.sessionId}`;
      n.close();
    };
  } catch {
    /* non-fatal */
  }
}
